import { BOTONES } from '../../../../../componentes/botones';
import { DatePipe } from '@angular/common';
import { Component, ViewEncapsulation, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { IConfigReenvio, IFilter, ILogReenvio } from 'modelos/src';
import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { LogReenviosService } from '../../../../../auxiliares/servicios/http/log-reenvios.service';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { VerBodyDialogComponent } from '../../log-reenvios/ver-body-dialog/ver-body-dialog.component';

export interface IEnviosEmergenciaAvlData {
  config: IConfigReenvio;
  /** Tracker a cuyo nombre están los envíos. Hace falta en una config por
   *  vehículo (su tracker actual); si no viene, es la entidad de la config. */
  idTracker?: string;
  /** Patente / nombre del tracker, para el encabezado */
  nombreEntidad?: string;
}

const POR_PAGINA = 50;

/**
 * Envíos a Suite911 de UNA emergencia AVL: los LogReenvio del tracker con
 * `protocolo: 'Objetivo AVL'` dentro del período de la config. Cada fila abre
 * el detalle completo del log (headers, body, respuesta).
 *
 * El filtro entra por `idEntidad_1_fecha_1`, con una ventana acotada al
 * período de la emergencia: barata aunque la colección tenga millones de docs.
 */
@Component({
  selector: 'app-envios-emergencia-avl-dialog',
  templateUrl: './envios-emergencia-avl-dialog.component.html',
  styleUrl: './envios-emergencia-avl-dialog.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    DatePipe,
    MatCardModule,
    CardHeaderComponent,
    BOTONES,
    MatIconModule,
    MatProgressSpinnerModule,
    TooltipDirective,
  ],
})
export class EnviosEmergenciaAvlDialogComponent {
  public data = inject<IEnviosEmergenciaAvlData>(MAT_DIALOG_DATA);
  public dialogRef =
    inject<MatDialogRef<EnviosEmergenciaAvlDialogComponent>>(MatDialogRef);
  private service = inject(LogReenviosService);
  private formularios = inject(FormularioDialogService);

  public envios: ILogReenvio[] = [];
  public cargando = false;
  public hayMas = false;
  private pagina = 0;

  constructor() {
    this.cargar();
  }

  public cerrar(): void {
    this.dialogRef.close();
  }

  get aceptados(): number {
    return this.envios.filter((e) => e.ack).length;
  }

  get rechazados(): number {
    return this.envios.length - this.aceptados;
  }

  /** El deviceid que viajó (patente, o ID único en configs viejas), sacado
   *  del body del log. */
  public deviceid(e: ILogReenvio): string | undefined {
    try {
      return JSON.parse(e.body ?? '')?.deviceid;
    } catch {
      return undefined;
    }
  }

  private filtro(): IFilter<ILogReenvio> {
    const c = this.data.config;
    const fecha: Record<string, string> = {};
    if (c.periodoInicio) fecha['$gte'] = c.periodoInicio;
    // Con margen: el último envío se loguea unos segundos después del corte.
    if (c.periodoFin) {
      fecha['$lte'] = new Date(
        new Date(c.periodoFin).getTime() + 5 * 60000,
      ).toISOString();
    }
    return {
      idEntidad: this.data.idTracker ?? c.idEntidadReenvio,
      protocolo: 'Objetivo AVL',
      ...(Object.keys(fecha).length ? { fecha } : {}),
    } as IFilter<ILogReenvio>;
  }

  public async cargar(reiniciar = true): Promise<void> {
    if (this.cargando) return;
    this.cargando = true;
    if (reiniciar) this.pagina = 0;
    try {
      const listado = await this.service.getFiltered({
        filter: JSON.stringify(this.filtro()),
        select:
          'fecha ack error body host protocolo metodoHttp headers respuestaStatus respuesta idEntidad',
        sort: '-fecha',
        page: this.pagina,
        limit: POR_PAGINA,
        excludeTotalCount: true,
        // Los envíos se loguean a nombre del cliente del TRACKER, que suele
        // ser hijo del que declaró la emergencia. Sin esto el scope quedaba en
        // el cliente propio y el dialog salía vacío.
        includeChildren: true,
        childrenLevel: 0,
      });
      const datos = listado?.datos ?? [];
      this.envios = reiniciar ? datos : [...this.envios, ...datos];
      this.hayMas = datos.length === POR_PAGINA;
    } catch (error) {
      console.error('Error al listar los envíos de la emergencia AVL', error);
    } finally {
      this.cargando = false;
    }
  }

  public async cargarMas(): Promise<void> {
    this.pagina++;
    await this.cargar(false);
  }

  public verDetalle(e: ILogReenvio): void {
    this.formularios.abrir(VerBodyDialogComponent, {
      tamano: 'mediano',
      data: e,
      cerrarAfuera: true,
    });
  }
}
